import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Loader2, DatabaseZap, RotateCw, ChevronDown, FileSpreadsheet, Users, TrendingUp, Tags, ArrowLeftRight, FileJson, Trophy } from "lucide-react";
import { api, API } from "../lib/api";
import { WeeklyReportExportDialog } from "./WeeklyReportExport";

/**
 * Admin data-tools dropdown — houses Backup, Restore-from-backup,
 * and (optionally) Restore Roster into one compact menu button.
 */
export default function BackupControls({ onImportDone, onRestoreRoster, restoringRoster }) {
  const [busy, setBusy] = useState(null); // "export" | "import" | null
  const [open, setOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const menuRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const importWme = async () => {
    const tid = toast.loading("Importing WME NIL roster…");
    try {
      await api.startRosterImport("wme-nil");
      let st = {};
      for (let i = 0; i < 180; i++) {
        await new Promise(r => setTimeout(r, 3000));
        st = await api.rosterImportStatus("wme-nil");
        toast.loading(`Importing WME NIL roster… ${st.done || 0}/${st.total || "?"}`, { id: tid });
        if (st.status === "done") break;
      }
      toast.success(`WME NIL: ${st.added?.length || 0} added · ${st.skipped?.length || 0} already on tracker${st.failed?.length ? ` · ${st.failed.length} failed` : ""}`, { id: tid, duration: 8000 });
      onImportDone?.();
    } catch (e) {
      toast.error("Roster import failed", { id: tid, description: e?.response?.data?.detail || e.message });
    }
  };

  const doExport = async () => {
    setOpen(false); setBusy("export");
    try {
      const data = await api.exportBackup();
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `maximize-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const c = data.counts || {};
      toast.success("Backup downloaded", {
        description: `${c.players || 0} players · ${c.snapshots || 0} inventories · ${c.workouts || 0} workouts · ${c.recruitment_interests || 0} recruits · ${c.film_reviews || 0} film reviews`,
        duration: 6000,
      });
    } catch (e) { toast.error("Export failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(null); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setBusy("import");
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== "object" || !Array.isArray(data.players)) {
        throw new Error("File doesn't look like a Maximize backup (missing 'players' array).");
      }
      if (!window.confirm(
        `Restore ${data.counts?.players ?? data.players.length} players ` +
        `and their inventories / workouts / recruitment / film from this backup?\n\n` +
        `Existing records with matching IDs will be updated. Nothing is deleted.`
      )) { setBusy(null); return; }
      const res = await api.importBackup({
        players:               data.players || [],
        workouts:              data.workouts || [],
        snapshots:             data.snapshots || [],
        recruitment_interests: data.recruitment_interests || [],
        film_reviews:          data.film_reviews || [],
      });
      const c = res.restored || {};
      toast.success("Restored from backup", {
        description: `${c.players || 0} players · ${c.snapshots || 0} inventories · ${c.workouts || 0} workouts · ${c.recruitment_interests || 0} recruits · ${c.film_reviews || 0} film reviews`,
        duration: 8000,
      });
      onImportDone && onImportDone();
    } catch (e) { toast.error("Restore failed", { description: e?.response?.data?.detail || e.message }); }
    finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const currentAction = busy === "export" ? "Exporting…"
                      : busy === "import" ? "Restoring…"
                      : restoringRoster    ? "Restoring…"
                      :                      null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy !== null || restoringRoster}
        className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 h-9 sm:py-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-60"
        style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
        data-testid="data-tools-btn"
      >
        {currentAction
          ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
          : <DatabaseZap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
        <span className="hidden sm:inline">{currentAction || "Data Tools"}</span>
        <ChevronDown className="w-3 h-3 shrink-0" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[260px] max-h-[80vh] overflow-y-auto bg-[#141414] border border-white/15 shadow-xl z-50" data-testid="data-tools-menu">
          <MenuItem
            Icon={Download}
            title="Backup"
            desc="Download a full JSON of every player, inventory, workout, recruitment note, and film review."
            onClick={doExport}
            testId="backup-export-btn"
          />
          <MenuItem
            Icon={Upload}
            title="Restore from Backup"
            desc="Load a previously-saved backup file. Existing records update; nothing is deleted."
            onClick={() => { setOpen(false); fileRef.current?.click(); }}
            testId="backup-import-btn"
          />
          <MenuItem
            Icon={FileSpreadsheet}
            title="Master Spreadsheet"
            desc="Download Development_Master_Lists.xlsx — kept in sync with every skill / archetype / role / film-tag library update."
            onClick={() => { setOpen(false); window.open(`${API}/admin/master-spreadsheet`, "_blank"); }}
            testId="master-spreadsheet-btn"
          />
          <MenuItem
            Icon={Users}
            title="Import WME NIL Roster"
            desc="Add WME's college NIL clients (77 players) under the WME NIL section with ESPN headshots + stats. Players already on the tracker are skipped."
            onClick={() => { setOpen(false); importWme(); }}
            testId="import-wme-btn"
          />
          <MenuItem
            Icon={TrendingUp}
            title="Email WME Movers Now"
            desc="Send this week's WME NIL trending up / down digest to all admins (also goes out automatically Mondays 8am ET)."
            onClick={async () => { setOpen(false); const tid = toast.loading("Building WME Movers email…"); try { const r = await api.sendWmeMoversEmail(); toast.success("WME Movers email is being built — it reaches every admin in about a minute (skipped if nobody moved this week)", { id: tid, duration: 8000 }); } catch (e) { toast.error("Could not send", { id: tid, description: e?.response?.data?.detail || e.message }); } }}
            testId="wme-movers-email-btn"
          />
          <MenuItem
            Icon={ArrowLeftRight}
            title="Sync Teams Now"
            desc="Check every NBA / NCAA player's current team against balldontlie and apply transfers / trades (runs automatically daily 6am ET; prior seasons keep their stats)."
            onClick={async () => { setOpen(false); const tid = toast.loading("Checking teams…"); try { const r = await api.teamSync(); const ch = r.changes || []; toast.success(ch.length ? `${ch.length} team change${ch.length > 1 ? "s" : ""} applied` : `All ${r.checked} players already on the right team`, { id: tid, duration: 8000, description: ch.map(c => `${c.name}: ${c.from} → ${c.to}`).join(" · ") || undefined }); if (ch.length) onImportDone?.(); } catch (e) { toast.error("Team sync failed", { id: tid, description: e?.response?.data?.detail || e.message }); } }}
            testId="team-sync-btn"
          />
          <MenuItem
            Icon={FileJson}
            title="Export Weekly Report Data"
            desc="Download one JSON file with every college / pro player's season line and box scores for a date range (defaults to last Mon–Sun) — feeds the weekly Player Report graphic."
            onClick={() => { setOpen(false); setWeeklyOpen(true); }}
            testId="weekly-report-export-btn"
          />
          <MenuItem
            Icon={Trophy}
            title="Weekly Wins"
            desc="Monday social graphic: auto-picks last week's standout performances, you review / swap / approve, download 1080×1350 PNGs."
            onClick={() => { setOpen(false); window.location.assign("/weekly-wins"); }}
            testId="weekly-wins-btn"
          />
          <MenuItem
            Icon={Tags}
            title="Tag Finishing Toolbox"
            desc="Match the 24 Finishing Toolbox rows to the IQ Library videos by step + exact title and save TB- IDs + tags on each video (adds fields only; nothing moved or renamed). Reports any title that didn't match."
            onClick={async () => {
              setOpen(false); const tid = toast.loading("Checking Finishing Toolbox…");
              try {
                const dry = await api.finishingToolboxReport();
                if (dry.reason) { toast.error(dry.reason, { id: tid, duration: 9000 }); return; }
                const lines = [`${dry.matched.length} of ${dry.matched.length + dry.unmatched_rows.length} rows matched`, ...(dry.unmatched_rows.length ? [`Unmatched rows: ${dry.unmatched_rows.join("; ")}`] : []), ...(dry.videos_not_in_csv.length ? [`Videos not in CSV: ${dry.videos_not_in_csv.map(v => `${v.step} · ${v.title}`).join("; ")}`] : [])];
                if (!window.confirm(`${lines.join("\n")}\n\nApply tags to the ${dry.matched.length} matched videos now?`)) { toast.dismiss(tid); return; }
                const r = await api.tagFinishingToolbox();
                toast[r.ok ? "success" : "warning"](`Tagged ${r.matched.length} Toolbox videos${r.unmatched_rows.length ? ` · ${r.unmatched_rows.length} rows unmatched` : ""}${r.videos_not_in_csv.length ? ` · ${r.videos_not_in_csv.length} videos not in CSV` : ""}`, { id: tid, duration: 10000, description: [...r.unmatched_rows, ...r.videos_not_in_csv.map(v => `Not in CSV: ${v.step} · ${v.title}`)].join(" | ") || undefined });
              } catch (e) { toast.error("Toolbox tagging failed", { id: tid, description: e?.response?.data?.detail || e.message }); }
            }}
            testId="tag-finishing-toolbox-btn"
          />
          {onRestoreRoster && (
            <MenuItem
              Icon={RotateCw}
              title="Restore Roster"
              desc="Add any missing NBA / NCAA / Women's NCAA / PRO starter players and refresh ESPN headshots + stats."
              onClick={() => { setOpen(false); onRestoreRoster(); }}
              testId="restore-roster-btn"
            />
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        onChange={(e) => doImport(e.target.files?.[0])}
        className="hidden"
        data-testid="backup-import-file"
      />
      <WeeklyReportExportDialog open={weeklyOpen} onOpenChange={setWeeklyOpen} />
    </div>
  );
}

function MenuItem({ Icon, title, desc, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.05] border-b border-white/5 last:border-b-0"
      data-testid={testId}
    >
      <Icon className="w-4 h-4 text-[var(--org-accent)] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white">{title}</div>
        <div className="text-[10px] text-[#a0a0a0] mt-1 leading-relaxed">{desc}</div>
      </div>
    </button>
  );
}
